import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import apiClient from "../../../services/api";
import { useToast } from "../../../contexts/ToastContext";
import {
  AlertItem,
  AlertPreset,
  AlertType,
  DigestFrequency,
  DIGEST_FREQUENCY_LABELS,
  DAY_OF_WEEK_LABELS,
  CreateAlertPayload,
  UpdateAlertPayload,
  AlertFilters,
  NotificationSchedule,
} from "../../../types/grants";
import MultiSelectDropdown, { Option } from "../../common/MultiSelectDropdown";
import { hasModuleAccess } from "../../../lib/modules";

interface CreateAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingAlert: AlertItem | null;
  /** Pre-filled filters when creating from "Save search as alert" button */
  initialFilters?: Partial<AlertFilters>;
  /** Reference to the button that opened the modal (for focus management) */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
  /**
   * Remaining digest slots (limit: 1 per account).
   * When <= 0, the UPCOMING_DIGEST radio option is disabled.
   * When undefined (old backend), treated as available.
   */
  digestRemaining?: number;
  /**
   * Whether the user already has an active (enabled) UPCOMING_DIGEST alert.
   * Disambiguates why the digest option is disabled: an existing digest
   * ("limit reached") vs. no digest entitlement on the current plan
   * (`maxDigestAlertsPerUser = 0`). Without this, both states showed the same
   * misleading "Masz już aktywne cykliczne podsumowanie" copy.
   */
  hasActiveDigest?: boolean;
  /**
   * AC-8b: Enabled modules for the current tenant.
   * Used to filter the programs dropdown to schemes the tenant can access,
   * and to hide the "Powiaty (KFS)" section for BUR-only tenants.
   * When undefined, all programs are shown (backward compat).
   */
  enabledModules?: string[];
}

/**
 * Alert type options
 *
 * @see docs/specifications/2026-05-26-upcoming-calls-digest-alert/spec.md
 */
const ALERT_TYPE_OPTIONS: { value: AlertType; label: string; description: string }[] = [
  {
    value: AlertType.EVENT_DRIVEN,
    label: "Alert o zdarzeniach",
    description: "Powiadomienia gdy pojawi się nowy lub zmieniony nabór",
  },
  {
    value: AlertType.UPCOMING_DIGEST,
    label: "Nadchodzące nabory",
    description: "Cykliczne podsumowanie naborów otwierających się w ciągu 30 dni",
  },
];

/**
 * Preset options for simplified alert configuration
 *
 * @see docs/specifications/2026-01-22-alerts-ux-simplification.md
 */
const PRESET_OPTIONS: { value: AlertPreset; label: string; description: string }[] = [
  {
    value: AlertPreset.NEW_CALLS_ONLY,
    label: "Tylko nowe nabory",
    description: "Powiadomienia tylko o nowo otwartych naborach",
  },
  {
    value: AlertPreset.NEW_AND_CHANGES,
    label: "Nowe nabory i zmiany",
    description: "Nowe nabory oraz aktualizacje istniejących (np. zmiana daty, statusu)",
  },
  {
    value: AlertPreset.EVERYTHING,
    label: "Wszystko",
    description: "Wszystkie zdarzenia włącznie z nowymi komunikatami operatorów",
  },
];

/**
 * Common Polish regions
 */
const REGION_OPTIONS = [
  "dolnośląskie",
  "kujawsko-pomorskie",
  "lubelskie",
  "lubuskie",
  "łódzkie",
  "małopolskie",
  "mazowieckie",
  "opolskie",
  "podkarpackie",
  "podlaskie",
  "pomorskie",
  "śląskie",
  "świętokrzyskie",
  "warmińsko-mazurskie",
  "wielkopolskie",
  "zachodniopomorskie",
];

/**
 * Hours for scheduled notifications
 */
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

/** Digest frequency dropdown options */
const FREQUENCY_OPTIONS: { value: DigestFrequency; label: string }[] = [
  { value: DigestFrequency.WEEKLY, label: DIGEST_FREQUENCY_LABELS[DigestFrequency.WEEKLY] },
  { value: DigestFrequency.BIWEEKLY, label: DIGEST_FREQUENCY_LABELS[DigestFrequency.BIWEEKLY] },
  { value: DigestFrequency.FOUR_WEEKS, label: DIGEST_FREQUENCY_LABELS[DigestFrequency.FOUR_WEEKS] },
];

/** Day of week dropdown options (Monday first for Polish UX) */
const DAY_OF_WEEK_OPTIONS: { value: number; label: string }[] = [1, 2, 3, 4, 5, 6, 0].map((d) => ({
  value: d,
  label: DAY_OF_WEEK_LABELS[d],
}));

/**
 * Compute the className for an alert-type option container.
 *
 * Two independent dimensions:
 * - `isDisabled`: the option is locked (editing immutable type, or limit/plan block)
 *   → adds reduced opacity + not-allowed cursor.
 * - `isSelected`: the option is the currently chosen alert type → primary highlight.
 */
const getOptionClassName = (isDisabled: boolean, isSelected: boolean): string => {
  const base = "flex items-start gap-3 p-3 rounded-md border transition-colors";
  const selectionClasses = isSelected
    ? "border-primary-500 bg-primary-50"
    : isDisabled
      ? "border-neutral-200 bg-neutral-50"
      : "border-neutral-200 hover:border-neutral-300";
  const stateClasses = isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer";
  return `${base} ${selectionClasses} ${stateClasses}`;
};

/** KFS = NATIONAL_FUND programs; BUR = everything else (incl. null program) */
const PROGRAM_TYPE_KFS = "NATIONAL_FUND";

interface ProgramOption {
  id: string;
  code: string;
  name: string;
  /** program.type from backend — used for display-filtering only (NOT access control) */
  type?: string | null;
}

interface OperatorOption {
  id: string;
  name: string;
  shortName: string | null;
}

interface CompetitionOption {
  id: string;
  name: string;
  code: string | null;
  program?: {
    id: string;
    code: string;
  } | null;
}

interface ProjectOption {
  id: string;
  name: string;
  competition?: {
    id: string;
    name: string;
  } | null;
  operator?: {
    id: string;
    name: string;
    shortName: string | null;
  } | null;
}

/**
 * Modal for creating or editing an alert (event-driven or upcoming digest).
 *
 * @see docs/specifications/2026-05-26-upcoming-calls-digest-alert/spec.md
 */
const CreateAlertModal: React.FC<CreateAlertModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editingAlert,
  initialFilters,
  triggerRef,
  digestRemaining,
  hasActiveDigest = false,
  enabledModules,
}) => {
  const { addToast } = useToast();
  const isEditing = !!editingAlert;

  // Alert type
  const [alertType, setAlertType] = useState<AlertType>(AlertType.EVENT_DRIVEN);

  // Form state — shared
  const [name, setName] = useState("");
  const [scheduleMode, setScheduleMode] = useState<"IMMEDIATE" | "SCHEDULED">("SCHEDULED");
  const [scheduleHours, setScheduleHours] = useState<number[]>([8, 13]);
  const [timezone] = useState("Europe/Warsaw");

  // Event-driven specific
  const [preset, setPreset] = useState<AlertPreset>(AlertPreset.NEW_AND_CHANGES);

  // Digest specific
  const [frequency, setFrequency] = useState<DigestFrequency>(DigestFrequency.WEEKLY);
  const [dayOfWeek, setDayOfWeek] = useState<number>(1); // Monday default

  // Filters
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  const [selectedOperators, setSelectedOperators] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  // @see docs/specifications/2026-01-23-alert-filters-competitions-projects.md
  const [selectedCompetitions, setSelectedCompetitions] = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  // District slugs (KFS filtering)
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  // @deprecated - allocation fields hidden in UI but kept for backward compatibility
  const [minAllocation, setMinAllocation] = useState<string>("");
  const [maxAllocation, setMaxAllocation] = useState<string>("");

  // Fetch districts for dropdown (KFS)
  const { data: districtsData, isLoading: isLoadingDistricts } = useQuery<{
    districts: { slug: string; name: string; voivodeship: string; code: string }[];
    total: number;
  }>({
    queryKey: ["grants-districts-list"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/districts");
      return data;
    },
    enabled: isOpen,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - static data
  });

  // Fetch programs for dropdown
  const { data: programsData } = useQuery<{ programs: ProgramOption[] }>({
    queryKey: ["grants-programs-list"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/programs?limit=100");
      return data;
    },
    enabled: isOpen,
  });

  // Fetch operators for dropdown
  const { data: operatorsData } = useQuery<{ operators: OperatorOption[] }>({
    queryKey: ["grants-operators-list"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/operators?limit=100");
      return data;
    },
    enabled: isOpen,
  });

  // Fetch competitions for dropdown
  // @see docs/specifications/2026-01-23-alert-filters-competitions-projects.md
  const { data: competitionsData, isLoading: isLoadingCompetitions } = useQuery<{ competitions: CompetitionOption[] }>({
    queryKey: ["grants-competitions-list"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/competitions");
      return data;
    },
    enabled: isOpen,
    staleTime: 15 * 60 * 1000, // 15 minutes - prevent excessive API calls
  });

  // Fetch projects for dropdown
  // @see docs/specifications/2026-01-23-alert-filters-competitions-projects.md
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery<{ projects: ProjectOption[] }>({
    queryKey: ["grants-projects-list"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/projects");
      return data;
    },
    enabled: isOpen,
    staleTime: 15 * 60 * 1000, // 15 minutes - prevent excessive API calls
  });

  // Max 20 selection limit for competitions and projects
  const MAX_FILTER_SELECTIONS = 20;

  // AC-8b: Scheme-aware module access (display-filtering only — NOT access control)
  // Backend is single source of truth for access enforcement.
  const isKfsEnabled =
    enabledModules === undefined ||
    hasModuleAccess(enabledModules, "grants:kfs");
  const isBurEnabled =
    enabledModules === undefined ||
    hasModuleAccess(enabledModules, "grants:bur");
  const isBurOnly = isBurEnabled && !isKfsEnabled;

  // Filter programs list to only show programs matching tenant's entitled schemes.
  // Uses program.type for display-filtering (not access gating — backend enforces).
  const filteredPrograms = (programsData?.programs || []).filter((program) => {
    if (isKfsEnabled && isBurEnabled) return true; // ALL: show everything
    if (isKfsEnabled) return program.type === PROGRAM_TYPE_KFS; // KFS-only
    if (isBurEnabled) return program.type !== PROGRAM_TYPE_KFS; // BUR-only (incl. null)
    return false; // No module — show nothing (edge case)
  });

  // Convert competitions to MultiSelectDropdown options
  const competitionOptions: Option[] = (competitionsData?.competitions || []).map((c) => ({
    value: c.id,
    label: c.program?.code ? `[${c.program.code}] ${c.name}` : c.name,
  }));

  // Convert projects to MultiSelectDropdown options
  const projectOptions: Option[] = (projectsData?.projects || []).map((p) => ({
    value: p.id,
    label: p.operator?.shortName ? `[${p.operator.shortName}] ${p.name}` : p.name,
  }));

  // Convert districts to MultiSelectDropdown options
  const districtOptions: Option[] = (districtsData?.districts || []).map((d) => ({
    value: d.slug,
    label: d.name,
  }));

  // Handler for district selection with max 100 validation
  const handleDistrictChange = (values: string[]) => {
    if (values.length > 100) {
      addToast({
        message: "Maksymalnie można wybrać 100 powiatów",
        type: "error",
      });
      return;
    }
    setSelectedDistricts(values);
  };

  // Handler for competition selection with max 20 validation
  const handleCompetitionChange = (values: string[]) => {
    if (values.length > MAX_FILTER_SELECTIONS) {
      addToast({
        message: `Maksymalnie można wybrać ${MAX_FILTER_SELECTIONS} konkursów`,
        type: "error",
      });
      return;
    }
    setSelectedCompetitions(values);
  };

  // Handler for project selection with max 20 validation
  const handleProjectChange = (values: string[]) => {
    if (values.length > MAX_FILTER_SELECTIONS) {
      addToast({
        message: `Maksymalnie można wybrać ${MAX_FILTER_SELECTIONS} projektów`,
        type: "error",
      });
      return;
    }
    setSelectedProjects(values);
  };

  // Reset form when opening/closing or when editing alert changes
  useEffect(() => {
    if (isOpen) {
      if (editingAlert) {
        setAlertType(editingAlert.type ?? AlertType.EVENT_DRIVEN);
        setName(editingAlert.name);
        setPreset(editingAlert.preset || AlertPreset.NEW_AND_CHANGES);
        setFrequency((editingAlert.frequency as DigestFrequency) ?? DigestFrequency.WEEKLY);
        setDayOfWeek(editingAlert.dayOfWeek ?? 1);
        setScheduleMode(editingAlert.schedule.mode);
        setScheduleHours(editingAlert.schedule.hours);
        setSelectedPrograms(editingAlert.filters.programs || []);
        setSelectedOperators(editingAlert.filters.operators || []);
        setSelectedRegions(editingAlert.filters.regions || []);
        // NEW: Competition and Project filters
        setSelectedCompetitions(editingAlert.filters.competitionIds || []);
        setSelectedProjects(editingAlert.filters.projectIds || []);
        // District filters (KFS)
        setSelectedDistricts(editingAlert.filters.districtSlugs || []);
        // Deprecated but kept for backward compatibility
        setMinAllocation(editingAlert.filters.minAllocation?.toString() || "");
        setMaxAllocation(editingAlert.filters.maxAllocation?.toString() || "");
      } else {
        // Reset to defaults, with optional pre-filled filters from "Save search as alert"
        setAlertType(AlertType.EVENT_DRIVEN);
        setName("");
        setPreset(AlertPreset.NEW_AND_CHANGES);
        setFrequency(DigestFrequency.WEEKLY);
        setDayOfWeek(1);
        setScheduleMode("SCHEDULED");
        setScheduleHours([8, 13]);
        setSelectedPrograms(initialFilters?.programs || []);
        setSelectedOperators(initialFilters?.operators || []);
        setSelectedRegions(initialFilters?.regions || []);
        // NEW: Competition and Project filters
        setSelectedCompetitions(initialFilters?.competitionIds || []);
        setSelectedProjects(initialFilters?.projectIds || []);
        // District filters (KFS)
        setSelectedDistricts(initialFilters?.districtSlugs || []);
        // Deprecated but kept for backward compatibility
        setMinAllocation(initialFilters?.minAllocation?.toString() || "");
        setMaxAllocation(initialFilters?.maxAllocation?.toString() || "");
      }
    }
  }, [isOpen, editingAlert, initialFilters]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (payload: CreateAlertPayload) => {
      const { data } = await apiClient.post("/grants/alerts", payload);
      return data;
    },
    onSuccess: () => {
      addToast({ message: "Alert utworzony", type: "success" });
      onSuccess();
    },
    onError: (error: AxiosError<{ message?: string | string[] }>) => {
      const message = error.response?.data?.message || "Nie udało się utworzyć alertu";
      addToast({ message: Array.isArray(message) ? message[0] : message, type: "error" });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (payload: UpdateAlertPayload) => {
      const { data } = await apiClient.put(`/grants/alerts/${editingAlert!.id}`, payload);
      return data;
    },
    onSuccess: () => {
      addToast({ message: "Alert zaktualizowany", type: "success" });
      onSuccess();
    },
    onError: (error: AxiosError<{ message?: string | string[] }>) => {
      const message = error.response?.data?.message || "Nie udało się zaktualizować alertu";
      addToast({ message: Array.isArray(message) ? message[0] : message, type: "error" });
    },
  });

  const MAX_SCHEDULE_HOURS = 3;

  const handleHourToggle = (hour: number) => {
    setScheduleHours((prev) => {
      if (prev.includes(hour)) {
        return prev.filter((h) => h !== hour);
      }
      // Limit to MAX_SCHEDULE_HOURS
      if (prev.length >= MAX_SCHEDULE_HOURS) {
        return prev;
      }
      return [...prev, hour];
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Trim name to prevent whitespace-only submissions
    const trimmedName = name.trim();

    const filters: AlertFilters = {
      programs: selectedPrograms.length > 0 ? selectedPrograms : undefined,
      operators: selectedOperators.length > 0 ? selectedOperators : undefined,
      regions: selectedRegions.length > 0 ? selectedRegions : undefined,
      // NEW: Competition and Project filters
      competitionIds: selectedCompetitions.length > 0 ? selectedCompetitions : undefined,
      projectIds: selectedProjects.length > 0 ? selectedProjects : undefined,
      // District filters (KFS)
      districtSlugs: selectedDistricts.length > 0 ? selectedDistricts : undefined,
      // @deprecated - allocation fields hidden in UI but kept for backward compatibility
      minAllocation: minAllocation ? parseInt(minAllocation, 10) : undefined,
      maxAllocation: maxAllocation ? parseInt(maxAllocation, 10) : undefined,
    };

    const schedule: NotificationSchedule = {
      mode: scheduleMode,
      hours: scheduleMode === "SCHEDULED" ? scheduleHours : [],
      timezone,
    };

    if (isEditing) {
      const payload: UpdateAlertPayload =
        alertType === AlertType.UPCOMING_DIGEST
          ? { name: trimmedName, type: alertType, frequency, dayOfWeek, filters, schedule }
          : { name: trimmedName, type: alertType, preset, filters, schedule };
      updateMutation.mutate(payload);
    } else {
      const payload: CreateAlertPayload =
        alertType === AlertType.UPCOMING_DIGEST
          ? { type: AlertType.UPCOMING_DIGEST, name: trimmedName, frequency, dayOfWeek, filters, schedule }
          : { type: AlertType.EVENT_DRIVEN, name: trimmedName, preset, filters, schedule };
      createMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  // Allocation validation: min must be <= max when both are provided
  const allocationError = (() => {
    const min = minAllocation ? parseInt(minAllocation, 10) : null;
    const max = maxAllocation ? parseInt(maxAllocation, 10) : null;
    if (min !== null && max !== null && min > max) {
      return "Minimalna alokacja nie może być większa niż maksymalna";
    }
    return null;
  })();

  // Digest-specific validation: frequency and dayOfWeek must be set
  const isDigestValid =
    alertType !== AlertType.UPCOMING_DIGEST ||
    (frequency !== null && frequency !== undefined && dayOfWeek !== null && dayOfWeek !== undefined);

  // For UPCOMING_DIGEST the schedule is fixed (CRON 08:00 Warsaw) — skip hour validation
  const isScheduleValid =
    alertType === AlertType.UPCOMING_DIGEST ||
    scheduleMode === "IMMEDIATE" ||
    scheduleHours.length > 0;

  const isValid =
    name.trim().length > 0 &&
    isScheduleValid &&
    !allocationError &&
    isDigestValid;

  // Check if no filters are selected — based on current form state (not initialFilters)
  const hasNoFilters =
    selectedPrograms.length === 0 &&
    selectedOperators.length === 0 &&
    selectedRegions.length === 0 &&
    selectedCompetitions.length === 0 &&
    selectedProjects.length === 0 &&
    selectedDistricts.length === 0 &&
    !minAllocation &&
    !maxAllocation;

  // Show empty filters warning only when opened from "Save search as alert" with no filters
  const showEmptyFiltersWarning = !isEditing && initialFilters !== undefined && hasNoFilters;

  // Whether the UPCOMING_DIGEST option is disabled (limit reached, unless editing existing digest)
  const isDigestLimitReached =
    !isEditing &&
    digestRemaining !== undefined &&
    digestRemaining <= 0;

  // Compute the first send date hint based on selected dayOfWeek
  const firstSendDate = (() => {
    const today = new Date();
    const todayDow = today.getDay(); // 0=Sunday
    let daysUntil = (dayOfWeek - todayDow + 7) % 7;
    // If today IS the selected day, first send is next week (anchor logic)
    if (daysUntil === 0) {
      daysUntil = 7;
    }
    const date = new Date(today);
    date.setDate(today.getDate() + daysUntil);
    return date.toLocaleDateString('pl-PL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  })();

  // Handle close with focus management
  const handleClose = useCallback(() => {
    onClose();
    // Return focus to trigger button after modal closes
    // Using requestAnimationFrame for better timing than setTimeout(0)
    requestAnimationFrame(() => {
      triggerRef?.current?.focus();
    });
  }, [onClose, triggerRef]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSubmitting) {
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isSubmitting, handleClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h2 id="modal-title" className="text-lg font-medium text-neutral-900">
            {isEditing ? "Edytuj alert" : "Nowy alert"}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-neutral-400 hover:text-neutral-500"
            aria-label="Zamknij"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Empty filters warning */}
        {showEmptyFiltersWarning && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4" role="status">
            <div className="flex">
              <svg className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-blue-700">
                Nie wybrano filtrów. Ten alert będzie wysyłał powiadomienia o wszystkich nowych naborach.
              </p>
            </div>
          </div>
        )}

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-neutral-700">
            Nazwa alertu *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            placeholder="np. Nabory z mojego regionu"
            required
          />
        </div>

        {/* Alert type selector */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Typ alertu
          </label>
          {/* Immutability note — placed directly under the section label so it
              sits next to the locked options it explains (a11y: referenced via
              aria-describedby on each disabled <label>). */}
          {isEditing && (
            <p id="alert-type-immutable-note" className="mb-3 text-xs text-neutral-500">
              Typ alertu nie może być zmieniony po utworzeniu. Aby zmienić typ,
              usuń ten alert i utwórz nowy.
            </p>
          )}
          <div className="space-y-3">
            {ALERT_TYPE_OPTIONS.map((option) => {
              const isDigestOption = option.value === AlertType.UPCOMING_DIGEST;
              // Type is an immutable discriminator (ADR-076): when editing, the
              // radios are locked so the alert type cannot be switched. When
              // creating, the digest option is disabled if the limit/plan blocks it.
              const isLimitBlocked = isDigestOption && isDigestLimitReached;
              const isOptionDisabled = isEditing || isLimitBlocked;
              const isSelected = alertType === option.value;
              return (
                <label
                  key={option.value}
                  className={getOptionClassName(isOptionDisabled, isSelected)}
                  aria-disabled={isOptionDisabled || undefined}
                  aria-describedby={isEditing ? "alert-type-immutable-note" : undefined}
                >
                  <input
                    type="radio"
                    name="alertType"
                    value={option.value}
                    checked={isSelected}
                    onChange={(e) => setAlertType(e.target.value as AlertType)}
                    disabled={isOptionDisabled}
                    className="mt-0.5 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="text-left">
                    <span className="block text-sm font-medium text-neutral-900">
                      {option.label}
                    </span>
                    <span className="block text-sm text-neutral-500">
                      {option.description}
                    </span>
                    {!isEditing && isLimitBlocked && (
                      <span className="block text-xs text-amber-700 mt-1" role="status">
                        {hasActiveDigest ? (
                          "Masz już aktywne cykliczne podsumowanie. Limit: 1 na konto."
                        ) : (
                          <>
                            Cykliczne podsumowanie nie jest dostępne w Twoim planie.{" "}
                            <Link
                              to="/tenant/subscription"
                              className="font-medium text-amber-800 underline hover:text-amber-900"
                            >
                              Sprawdź dostępne plany
                            </Link>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Event-driven: Preset */}
        {alertType === AlertType.EVENT_DRIVEN && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              O czym chcesz być powiadamiany?
            </label>
            <div className="space-y-3">
              {PRESET_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                    preset === option.value
                      ? "border-primary-500 bg-primary-50"
                      : "border-neutral-200 hover:border-neutral-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="preset"
                    value={option.value}
                    checked={preset === option.value}
                    onChange={(e) => setPreset(e.target.value as AlertPreset)}
                    className="mt-0.5 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="text-left">
                    <span className="block text-sm font-medium text-neutral-900">
                      {option.label}
                    </span>
                    <span className="block text-sm text-neutral-500">
                      {option.description}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Digest: Frequency + DayOfWeek */}
        {alertType === AlertType.UPCOMING_DIGEST && (
          <div className="space-y-4">
            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <svg className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-blue-700">
                  Otrzymasz zestawienie naborów otwierających się w ciągu najbliższych 30 dni, spełniających wybrane filtry.
                </p>
              </div>
            </div>

            {/* Frequency */}
            <div>
              <label htmlFor="digest-frequency" className="block text-sm font-medium text-neutral-700 mb-1">
                Częstotliwość *
              </label>
              <select
                id="digest-frequency"
                value={frequency}
                onChange={(e) => setFrequency(Number(e.target.value) as DigestFrequency)}
                className="block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                required
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Day of week */}
            <div>
              <label htmlFor="digest-day-of-week" className="block text-sm font-medium text-neutral-700 mb-1">
                Dzień tygodnia *
              </label>
              <select
                id="digest-day-of-week"
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(Number(e.target.value))}
                className="block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                required
              >
                {DAY_OF_WEEK_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-neutral-500">
                Pierwsza wysyłka: {firstSendDate}.
              </p>
            </div>
          </div>
        )}

        {/* Schedule — hour picker (event-driven only; digest uses fixed CRON 08:00 Warsaw) */}
        {alertType === AlertType.UPCOMING_DIGEST ? (
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Harmonogram powiadomień
            </label>
            <p className="text-sm text-neutral-500">
              Podsumowanie wysyłane jest zawsze w wybrany dzień tygodnia o godzinie 8:00 rano (czas warszawski).
            </p>
          </div>
        ) : (
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Harmonogram powiadomień
          </label>
          <div className="space-y-3">
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={scheduleMode === "IMMEDIATE"}
                  onChange={() => setScheduleMode("IMMEDIATE")}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-neutral-700">Natychmiast (do 5 min)</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={scheduleMode === "SCHEDULED"}
                  onChange={() => setScheduleMode("SCHEDULED")}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-neutral-700">Zaplanowane godziny</span>
              </label>
            </div>

            {scheduleMode === "SCHEDULED" && (
              <div>
                <p className="text-sm text-neutral-500 mb-2">
                  Wybierz godziny wysyłki - max {MAX_SCHEDULE_HOURS} ({scheduleHours.length}/{MAX_SCHEDULE_HOURS})
                </p>
                <div className="flex flex-wrap gap-1" role="group" aria-label="Wybierz godziny wysyłki powiadomień">
                  {HOUR_OPTIONS.map((hour) => {
                    const isSelected = scheduleHours.includes(hour);
                    const isDisabled = !isSelected && scheduleHours.length >= MAX_SCHEDULE_HOURS;
                    return (
                      <button
                        key={hour}
                        type="button"
                        onClick={() => handleHourToggle(hour)}
                        disabled={isDisabled}
                        aria-label={`Godzina ${hour.toString().padStart(2, "0")}:00`}
                        aria-pressed={isSelected}
                        className={`w-10 h-8 text-sm rounded ${
                          isSelected
                            ? "bg-primary-600 text-white"
                            : isDisabled
                              ? "bg-neutral-50 text-neutral-300 cursor-not-allowed"
                              : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                        }`}
                      >
                        {hour.toString().padStart(2, "0")}
                      </button>
                    );
                  })}
                </div>
                {scheduleHours.length === 0 && (
                  <p className="mt-1 text-sm text-red-600">Wybierz co najmniej jedną godzinę</p>
                )}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Filters section */}
        <div className="border-t border-neutral-200 pt-4">
          <h4 className="text-sm font-medium text-neutral-700 mb-3">
            Filtry (opcjonalne)
          </h4>

          {/* Programs */}
          <div className="mb-4">
            <label className="block text-sm text-neutral-600 mb-1">Programy</label>
            <select
              multiple
              value={selectedPrograms}
              onChange={(e) =>
                setSelectedPrograms(
                  Array.from(e.target.selectedOptions, (option) => option.value)
                )
              }
              className="block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              size={4}
            >
              {filteredPrograms.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.code} - {program.name}
                </option>
              ))}
            </select>
            {/* AC-8b: scheme hint — shown when programs are filtered by module */}
            {isBurOnly && (
              <p className="mt-1 text-xs text-amber-600" data-testid="scheme-hint-bur-only" role="note">
                Twój plan obejmuje tylko nabory BUR — alerty KFS nie będą wysyłane.{" "}
                <Link to="/tenant/subscription" onClick={handleClose} className="underline font-medium hover:text-amber-700">
                  Odblokuj KFS
                </Link>
              </p>
            )}
            {isKfsEnabled && !isBurEnabled && enabledModules !== undefined && (
              <p className="mt-1 text-xs text-amber-600" data-testid="scheme-hint-kfs-only" role="note">
                Twój plan obejmuje tylko nabory KFS — alerty BUR nie będą wysyłane.{" "}
                <Link to="/tenant/subscription" onClick={handleClose} className="underline font-medium hover:text-amber-700">
                  Odblokuj BUR
                </Link>
              </p>
            )}
            {!isKfsEnabled && !isBurEnabled && (
              <p className="mt-1 text-xs text-red-600" data-testid="scheme-hint-no-module" role="note">
                Brak aktywnego modułu naborów. Wybierz plan z dostępem do KFS lub BUR.{" "}
                <Link to="/tenant/subscription" onClick={handleClose} className="underline font-medium hover:text-red-700">
                  Przejdź do subskrypcji
                </Link>
              </p>
            )}
            <p className="mt-1 text-xs text-neutral-500">
              Przytrzymaj Ctrl/Cmd aby wybrać wiele
            </p>
          </div>

          {/* Operators */}
          <div className="mb-4">
            <label className="block text-sm text-neutral-600 mb-1">Operatorzy</label>
            <select
              multiple
              value={selectedOperators}
              onChange={(e) =>
                setSelectedOperators(
                  Array.from(e.target.selectedOptions, (option) => option.value)
                )
              }
              className="block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              size={4}
            >
              {operatorsData?.operators?.map((operator) => (
                <option key={operator.id} value={operator.id}>
                  {operator.shortName || operator.name}
                </option>
              ))}
            </select>
          </div>

          {/* Regions */}
          <div className="mb-4">
            <label className="block text-sm text-neutral-600 mb-1">Regiony</label>
            <select
              multiple
              value={selectedRegions}
              onChange={(e) =>
                setSelectedRegions(
                  Array.from(e.target.selectedOptions, (option) => option.value)
                )
              }
              className="block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              size={4}
            >
              {REGION_OPTIONS.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </div>

          {/* Districts (KFS) - per docs/specifications/2026-01-27-kfs-phase3-alerts-by-district.md */}
          {/* AC-8b: hidden for BUR-only tenants (KFS districts irrelevant) */}
          {isKfsEnabled && (
            <div className="mb-4" data-testid="districts-kfs-section">
              <MultiSelectDropdown
                label="Powiaty (KFS)"
                options={districtOptions}
                selectedValues={selectedDistricts}
                onChange={handleDistrictChange}
                placeholder={isLoadingDistricts ? "Ładowanie..." : "Wybierz powiaty..."}
                disabled={isLoadingDistricts}
                ariaLabel="Wybierz powiaty"
              />
              <p className="mt-1 text-xs text-neutral-500">
                Filtruj alerty KFS po lokalnych urzędach pracy (PUP). Brak wyboru = wszystkie powiaty.
              </p>
            </div>
          )}

          {/* Competitions - NEW per docs/specifications/2026-01-23-alert-filters-competitions-projects.md */}
          <div className="mb-4">
            <MultiSelectDropdown
              label="Konkursy"
              options={competitionOptions}
              selectedValues={selectedCompetitions}
              onChange={handleCompetitionChange}
              placeholder={isLoadingCompetitions ? "Ładowanie..." : "Wybierz konkursy..."}
              disabled={isLoadingCompetitions}
              ariaLabel="Wybierz konkursy"
            />
            <p className="mt-1 text-xs text-neutral-500">
              Konkretne konkursy PARP (np. PSF2, Granty na Cyfryzację). Max {MAX_FILTER_SELECTIONS}.
            </p>
          </div>

          {/* Projects - NEW per docs/specifications/2026-01-23-alert-filters-competitions-projects.md */}
          <div className="mb-4">
            <MultiSelectDropdown
              label="Projekty"
              options={projectOptions}
              selectedValues={selectedProjects}
              onChange={handleProjectChange}
              placeholder={isLoadingProjects ? "Ładowanie..." : "Wybierz projekty..."}
              disabled={isLoadingProjects}
              ariaLabel="Wybierz projekty"
            />
            <p className="mt-1 text-xs text-neutral-500">
              Projekty operatorów (np. konkretna instytucja realizująca). Max {MAX_FILTER_SELECTIONS}.
            </p>
          </div>

          {/* Allocation range - HIDDEN per docs/specifications/2026-01-23-alert-filters-competitions-projects.md
            * Kept in code for backward compatibility but not displayed in UI */}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
          >
            Anuluj
          </button>
          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Zapisywanie..." : isEditing ? "Zapisz zmiany" : "Utwórz alert"}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAlertModal;
